import { useState } from 'react';
import { Lock, LockOpen, Search } from 'lucide-react';
import { PerfilPadrao, formatarDataHora, type UsuarioEmpresaDto } from '@nexital/shared';
import { useDependencias } from '../../application/dependencias';
import { Avatar } from '../design-system/Avatar';
import { useConfirmar, usePedirMotivo } from '../design-system/Confirmacao';
import { useCarga, useGravar } from './campos';

type Filtro = 'todos' | 'administradores' | 'gestores' | 'colaboradores' | 'leitores' | 'ativos' | 'bloqueados';

const tem = (u: UsuarioEmpresaDto, p: PerfilPadrao) => u.quadros.some((q) => q.perfilPadrao === p);
const FILTROS: Record<Filtro, { rotulo: string; aceita: (u: UsuarioEmpresaDto) => boolean }> = {
  todos: { rotulo: 'Todos', aceita: () => true },
  administradores: { rotulo: 'Administradores', aceita: (u) => u.administrador },
  gestores: { rotulo: 'Gestores', aceita: (u) => tem(u, PerfilPadrao.Gestor) },
  colaboradores: { rotulo: 'Colaboradores', aceita: (u) => tem(u, PerfilPadrao.Colaborador) },
  leitores: { rotulo: 'Leitores', aceita: (u) => tem(u, PerfilPadrao.Leitor) },
  ativos: { rotulo: 'Ativos', aceita: (u) => u.ativo },
  bloqueados: { rotulo: 'Bloqueados', aceita: (u) => !u.ativo },
};
const ORDEM_FILTROS = Object.keys(FILTROS) as Filtro[];

function Acao({ u, aoMudar }: { u: UsuarioEmpresaDto; aoMudar: () => void }) {
  const { configuracoes } = useDependencias();
  const pedirMotivo = usePedirMotivo();
  const confirmar = useConfirmar();
  const { gravar } = useGravar();
  const bloquear = async () => {
    const motivo = await pedirMotivo({ titulo: `Bloquear ${u.nome}?`, mensagem: 'A pessoa perde o acesso a todos os quadros da empresa na hora. Dá para desbloquear depois.', rotulo: 'Bloquear' });
    if (motivo && (await gravar(() => configuracoes.bloquear(u.uuid, motivo), `${u.nome} bloqueado.`))) aoMudar();
  };
  const desbloquear = async () => {
    const sim = await confirmar({ titulo: `Desbloquear ${u.nome}?`, mensagem: 'A pessoa volta a acessar os quadros em que tem perfil.', rotulo: 'Desbloquear' });
    if (sim && (await gravar(() => configuracoes.desbloquear(u.uuid), `${u.nome} desbloqueado.`))) aoMudar();
  };
  return u.ativo ? (
    <button type="button" className="botao botao--secundario" onClick={() => void bloquear()}>
      <Lock size={14} aria-hidden /> Bloquear
    </button>
  ) : (
    <button type="button" className="botao botao--secundario" onClick={() => void desbloquear()}>
      <LockOpen size={14} aria-hidden /> Desbloquear
    </button>
  );
}

function CartaoUsuario({ u, podeBloquear, aoMudar }: { u: UsuarioEmpresaDto; podeBloquear: boolean; aoMudar: () => void }) {
  return (
    <li className={`config__usuario${u.ativo ? '' : ' config__usuario--bloqueado'}`}>
      <Avatar nome={u.nome} chave={u.uuid} tamanho={40} url={u.avatarUrl} />
      <div className="config__usuario-dados">
        <strong>
          {u.nome} {u.administrador && <span className="pilula pilula--lilas">Administrador</span>}{' '}
          {!u.ativo && <span className="pilula pilula--urgencia">Bloqueado</span>}
        </strong>
        <span>{u.email}</span>
        <span className="config__usuario-quadros">
          {u.quadros.map((q) => (
            <span key={q.quadroUuid} className="pilula pilula--informacao" title={q.superior ? `Superior direto: ${q.superior}` : undefined}>
              {q.quadro}: {q.perfil}
              {q.superior && ` · ${q.superior}`}
            </span>
          ))}
        </span>
        <small>
          {u.ultimoAcesso ? `Último acesso em ${formatarDataHora(u.ultimoAcesso)}` : 'Nunca acessou'}
          {u.bloqueio && ` · Bloqueado em ${formatarDataHora(u.bloqueio.em)}${u.bloqueio.por ? ` por ${u.bloqueio.por}` : ''}${u.bloqueio.motivo ? `: ${u.bloqueio.motivo}` : ''}`}
        </small>
      </div>
      {podeBloquear && <Acao u={u} aoMudar={aoMudar} />}
    </li>
  );
}

/**
 * Usuários da empresa (ADR-0022): o administrador vê todos e bloqueia (vale para a empresa toda); o gestor vê a equipe
 * da qual é superior direto. O histórico do que cada um fez vem depois.
 */
export function SecaoUsuarios() {
  const { configuracoes } = useDependencias();
  const { dados, erro, recarregar } = useCarga(configuracoes.usuarios);
  const [filtro, definirFiltro] = useState<Filtro>('todos');
  const [busca, definirBusca] = useState('');
  if (erro) return <p className="cartao-acesso__erro">{erro}</p>;
  if (!dados) return <p>Carregando…</p>;
  const termo = busca.trim().toLowerCase();
  const lista = dados.usuarios.filter((u) => FILTROS[filtro].aceita(u) && (!termo || `${u.nome} ${u.email}`.toLowerCase().includes(termo)));
  return (
    <div className="config__formulario">
      <div className="config__filtros" role="group" aria-label="Filtrar usuários">
        {ORDEM_FILTROS.map((f) => (
          <button key={f} type="button" className="config__filtro" aria-pressed={filtro === f} onClick={() => definirFiltro(f)}>
            {FILTROS[f].rotulo} <span className="grupo__contador">{dados.usuarios.filter(FILTROS[f].aceita).length}</span>
          </button>
        ))}
        <label className="config__busca">
          <Search size={16} aria-hidden />
          <input value={busca} placeholder="Buscar por nome ou e-mail" aria-label="Buscar usuários" onChange={(e) => definirBusca(e.target.value)} />
        </label>
      </div>
      {lista.length === 0 ? (
        <p className="config__vazio">Ninguém neste filtro.</p>
      ) : (
        <ul className="config__usuarios">
          {lista.map((u) => (
            <CartaoUsuario key={u.uuid} u={u} podeBloquear={dados.podeBloquear} aoMudar={() => void recarregar()} />
          ))}
        </ul>
      )}
    </div>
  );
}
